import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@savvy-kit/components/button';
import { Text } from '@savvy-kit/foundations/text';
import { DateField } from '@savvy-kit/patterns/date-field';
import InputField from '@savvy-kit/patterns/input-field';
import { DateTime } from 'luxon';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { formatCurrencyForSubmit } from 'src/utils/currency';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { ASSET_CLASS_COMPANY_EQUITY } from '@savvy/constants/asset-classes';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import type { CompanyEquityFormValues } from './add-account-schemas';
import {
  ADD_ACCOUNT_SAVE_ERROR,
  EQUITY_ASSET_DESCRIPTION,
  EQUITY_BASICS_TITLE,
  EQUITY_GRANT_QUESTION_TITLE,
  EQUITY_GRANT_TITLE,
  EQUITY_GRANT_TYPE_OPTIONS,
  EQUITY_GRANT_TYPES_WITH_EXERCISE_PRICE,
  EQUITY_PUBLIC_ONLY_NOTE,
  EQUITY_TICKER_DESCRIPTION,
  EQUITY_VALUE_TITLE,
  EQUITY_VESTING_RATE_OPTIONS,
} from './add-account-constants';
import { AddAccountFormShell } from './add-account-form-shell';
import { CLIENT_PORTAL_CREATE_ASSET } from './add-account-graphql';
import { AddAccountNumericField } from './add-account-numeric-field';
import { companyEquityFormSchema } from './add-account-schemas';
import { AddAccountSelectField } from './add-account-select-field';

type EquityStep = 'basics' | 'grant-question' | 'grant' | 'value';

const BASICS_FIELDS = ['companyEquityCompanyName', 'companyEquityCompanyTicker', 'companyEquityCurrentPrice'] as const;

const GRANT_FIELDS = [
  'companyEquityGrantType',
  'companyEquitySharesIssued',
  'companyEquityExercisePrice',
  'companyEquityVestingStartDate',
  'companyEquityVestingDuration',
  'companyEquityVestingCliff',
  'companyEquityVestingRate',
] as const;

function tickerOnly(value: string) {
  return value.replace(/[^a-zA-Z]/g, '').toUpperCase();
}

type AddAccountCompanyEquityFormProps = {
  readonly onCancel: () => void;
  readonly onCreated: () => Promise<unknown>;
};

export function AddAccountCompanyEquityForm({ onCancel, onCreated }: AddAccountCompanyEquityFormProps) {
  const [createAsset, { loading }] = useMutationWithErrorLogging(CLIENT_PORTAL_CREATE_ASSET);
  const [step, setStep] = useState<EquityStep>('basics');
  const { control, handleSubmit, formState, trigger, setValue } = useForm<CompanyEquityFormValues>({
    resolver: zodResolver(companyEquityFormSchema),
    defaultValues: {
      companyEquityCompanyName: '',
      companyEquityCompanyTicker: '',
      companyEquityCurrentPrice: '',
      addGrantDetails: false,
      companyEquityGrantType: '',
      companyEquitySharesIssued: '',
      companyEquityExercisePrice: '',
      companyEquityVestingStartDate: undefined,
      companyEquityVestingDuration: '',
      companyEquityVestingCliff: '',
      companyEquityVestingRate: '',
      currentValue: '',
    },
  });
  const grantType = useWatch({ control, name: 'companyEquityGrantType' });
  const addGrantDetails = useWatch({ control, name: 'addGrantDetails' });

  const submit = handleSubmit(async (values) => {
    const grant = values.addGrantDetails;
    try {
      await createAsset({
        variables: {
          input: {
            assetClass: ASSET_CLASS_COMPANY_EQUITY,
            companyEquityCompanyName: values.companyEquityCompanyName,
            companyEquityCompanyTicker: values.companyEquityCompanyTicker,
            companyEquityCurrentPrice: formatCurrencyForSubmit(values.companyEquityCurrentPrice),
            companyEquityExercisePrice:
              grant && values.companyEquityExercisePrice !== ''
                ? formatCurrencyForSubmit(values.companyEquityExercisePrice)
                : undefined,
            companyEquityGrantType: grant ? values.companyEquityGrantType : undefined,
            companyEquitySharesIssued: grant ? values.companyEquitySharesIssued : undefined,
            companyEquityVestingCliff: grant ? values.companyEquityVestingCliff : undefined,
            companyEquityVestingDuration: grant ? values.companyEquityVestingDuration : undefined,
            companyEquityVestingRate: grant ? values.companyEquityVestingRate : undefined,
            companyEquityVestingStartDate:
              grant && values.companyEquityVestingStartDate !== undefined
                ? DateTime.fromJSDate(values.companyEquityVestingStartDate).toISODate()
                : undefined,
            currencyCode: 'USD',
            currentValue: formatCurrencyForSubmit(values.currentValue) ?? '',
            description: EQUITY_ASSET_DESCRIPTION,
          },
        },
      });
      await onCreated();
    } catch (error) {
      handleMutationError(error, ADD_ACCOUNT_SAVE_ERROR);
    }
  });

  if (step === 'basics') {
    return (
      <AddAccountFormShell
        title={EQUITY_BASICS_TITLE}
        onCancel={onCancel}
        onSubmit={() => {
          void trigger(BASICS_FIELDS).then((valid) => {
            if (valid) {
              setStep('grant-question');
            }
          });
        }}
        submitLabel="Next"
      >
        <Text variant="body-sm" color="secondary">
          {EQUITY_PUBLIC_ONLY_NOTE}
        </Text>
        <Controller
          control={control}
          name="companyEquityCompanyName"
          render={({ field }) => (
            <InputField
              {...field}
              id="add-equity-company-name"
              label="Company name"
              required
              autoFocus
              error={formState.errors.companyEquityCompanyName !== undefined}
              errorMessage={formState.errors.companyEquityCompanyName?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="companyEquityCompanyTicker"
          render={({ field }) => (
            <InputField
              {...field}
              id="add-equity-ticker"
              label="Company ticker"
              required
              description={EQUITY_TICKER_DESCRIPTION}
              onChange={(event) => {
                field.onChange(tickerOnly(event.target.value));
              }}
              error={formState.errors.companyEquityCompanyTicker !== undefined}
              errorMessage={formState.errors.companyEquityCompanyTicker?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="companyEquityCurrentPrice"
          render={({ field }) => (
            <AddAccountNumericField
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              maxDecimals={2}
              id="add-equity-current-price"
              label="Current price"
              required
              prependInputAddon="$"
              errorMessage={formState.errors.companyEquityCurrentPrice?.message}
            />
          )}
        />
      </AddAccountFormShell>
    );
  }

  if (step === 'grant-question') {
    return (
      <AddAccountFormShell
        title={EQUITY_GRANT_QUESTION_TITLE}
        onCancel={onCancel}
        onStepBack={() => {
          setStep('basics');
        }}
      >
        <Text variant="body-sm">
          Grant details let your team model vesting. You can skip this and add the value on its own.
        </Text>
        <div className="flex gap-3">
          <Button
            type="button"
            onClick={() => {
              setValue('addGrantDetails', true);
              setStep('grant');
            }}
          >
            Yes, add grant details
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setValue('addGrantDetails', false);
              setStep('value');
            }}
          >
            Skip
          </Button>
        </div>
      </AddAccountFormShell>
    );
  }

  if (step === 'grant') {
    return (
      <AddAccountFormShell
        title={EQUITY_GRANT_TITLE}
        onCancel={onCancel}
        onStepBack={() => {
          setStep('grant-question');
        }}
        onSubmit={() => {
          void trigger(GRANT_FIELDS).then((valid) => {
            if (valid) {
              setStep('value');
            }
          });
        }}
        submitLabel="Next"
      >
        <Controller
          control={control}
          name="companyEquityGrantType"
          render={({ field }) => (
            <AddAccountSelectField
              id="add-equity-grant-type"
              label="Grant type"
              value={field.value}
              options={EQUITY_GRANT_TYPE_OPTIONS}
              onValueChange={field.onChange}
              errorMessage={formState.errors.companyEquityGrantType?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="companyEquitySharesIssued"
          render={({ field }) => (
            <AddAccountNumericField
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              maxDecimals={6}
              id="add-equity-shares"
              label="Number of shares issued"
              required
              errorMessage={formState.errors.companyEquitySharesIssued?.message}
            />
          )}
        />
        {EQUITY_GRANT_TYPES_WITH_EXERCISE_PRICE.includes(grantType) && (
          <Controller
            control={control}
            name="companyEquityExercisePrice"
            render={({ field }) => (
              <AddAccountNumericField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                maxDecimals={2}
                id="add-equity-exercise-price"
                label="Exercise price"
                required
                prependInputAddon="$"
                errorMessage={formState.errors.companyEquityExercisePrice?.message}
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="companyEquityVestingStartDate"
          render={({ field }) => (
            <DateField
              id="add-equity-vesting-start"
              label="Vesting start date"
              required
              value={field.value}
              onChange={field.onChange}
              maxDate={new Date()}
              error={formState.errors.companyEquityVestingStartDate !== undefined}
              errorMessage={formState.errors.companyEquityVestingStartDate?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="companyEquityVestingDuration"
          render={({ field }) => (
            <AddAccountNumericField
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              maxDecimals={2}
              id="add-equity-vesting-duration"
              label="Vesting duration (years)"
              required
              errorMessage={formState.errors.companyEquityVestingDuration?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="companyEquityVestingCliff"
          render={({ field }) => (
            <AddAccountNumericField
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              maxDecimals={2}
              id="add-equity-vesting-cliff"
              label="Vesting cliff (years)"
              required
              errorMessage={formState.errors.companyEquityVestingCliff?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="companyEquityVestingRate"
          render={({ field }) => (
            <AddAccountSelectField
              id="add-equity-vesting-rate"
              label="Vesting rate"
              value={field.value}
              options={EQUITY_VESTING_RATE_OPTIONS}
              onValueChange={field.onChange}
              errorMessage={formState.errors.companyEquityVestingRate?.message}
            />
          )}
        />
      </AddAccountFormShell>
    );
  }

  return (
    <AddAccountFormShell
      title={EQUITY_VALUE_TITLE}
      onCancel={onCancel}
      onStepBack={() => {
        setStep(addGrantDetails ? 'grant' : 'grant-question');
      }}
      onSubmit={submit}
      submitLabel="Add equity"
      submitting={loading}
    >
      <Controller
        control={control}
        name="currentValue"
        render={({ field }) => (
          <AddAccountNumericField
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            maxDecimals={2}
            id="add-equity-value"
            label="Value"
            required
            prependInputAddon="$"
            errorMessage={formState.errors.currentValue?.message}
          />
        )}
      />
    </AddAccountFormShell>
  );
}
